import React, { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import { DailyCall, DailyEventObjectParticipant } from "@daily-co/daily-js";
import styles from "./CollapsedCallBar.module.scss";

// The call panel's phone form while a consult dialog is open: a thin bar
// docked at the top edge, above the dialog overlay, in place of the video
// window. The Daily frame itself stays mounted in the (hidden) panel, so
// the call and its audio keep running.
//
// Rendered into document.body: react-modal sets aria-hidden on #root while
// a dialog is open, and the bar must stay a reachable, labelled region.

export const CALL_BAR_HEIGHT_PX = 44;

// Read by the AccessibleDialog overlay and the consult dialog containers:
// set only while the bar is shown, so dialogs start below it and no scroll
// area ends up under it. Unset (the default everywhere else) means 0px.
export const CALL_BAR_OFFSET_VAR = "--video-call-bar-offset";

interface CollapsedCallBarProps {
  frameRef: React.MutableRefObject<DailyCall | null>;
  callerName: string;
  onShowVideo: () => void;
}

// daily-js returns null while there is no local participant yet (its
// typings say boolean): the toggle stays disabled until the state is known.
function readLocalAudio(frame: DailyCall): boolean | null {
  const value: boolean | null = frame.localAudio();
  return typeof value === "boolean" ? value : null;
}

// A pointer press would move focus out of the open dialog (and past its
// focus trap); the click still fires.
const keepDialogFocus = (event: React.MouseEvent) => event.preventDefault();

const MicIcon: React.FC<{ muted: boolean }> = ({ muted }) => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    aria-hidden="true"
    focusable="false"
  >
    <path
      fill="currentColor"
      d="M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2z"
    />
    {muted && (
      <path
        d="M4 4l16 16"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    )}
  </svg>
);

const CollapsedCallBar: React.FC<CollapsedCallBarProps> = ({
  frameRef,
  callerName,
  onShowVideo,
}) => {
  // The REAL microphone state, read from the frame and refreshed on every
  // local participant update (including a mute made in the Daily UI). A
  // click only asks Daily; the label follows the event that confirms it.
  const [micOn, setMicOn] = useState<boolean | null>(null);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const sync = () => setMicOn(readLocalAudio(frame));
    const handleParticipantUpdated = (event?: DailyEventObjectParticipant) => {
      if (event?.participant?.local) sync();
    };
    sync();
    frame.on("participant-updated", handleParticipantUpdated);
    return () => {
      frame.off("participant-updated", handleParticipantUpdated);
    };
  }, [frameRef]);

  // Before paint, so the dialog never renders a frame under the bar.
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(CALL_BAR_OFFSET_VAR, `${CALL_BAR_HEIGHT_PX}px`);
    return () => {
      root.style.removeProperty(CALL_BAR_OFFSET_VAR);
    };
  }, []);

  const handleToggleMic = () => {
    const frame = frameRef.current;
    if (!frame || micOn === null) return;
    frame.setLocalAudio(!micOn);
  };

  const micLabel = micOn === false ? "Uključi mikrofon" : "Isključi mikrofon";

  return createPortal(
    <div
      role="region"
      aria-label="Video poziv u toku"
      className={styles.bar}
      style={{ height: CALL_BAR_HEIGHT_PX }}
    >
      <span className={styles.liveDot} aria-hidden="true" />
      <span className={styles.callerName} title={callerName}>
        {callerName}
      </span>
      <button
        type="button"
        className={classNames(styles.micButton, {
          [styles.micOff]: micOn === false,
        })}
        onMouseDown={keepDialogFocus}
        onClick={handleToggleMic}
        disabled={micOn === null}
        aria-label={micLabel}
        title={micLabel}
      >
        <MicIcon muted={micOn === false} />
      </button>
      <button
        type="button"
        className={styles.showVideoButton}
        onMouseDown={keepDialogFocus}
        onClick={onShowVideo}
      >
        Prikaži video
      </button>
    </div>,
    document.body
  );
};

export default CollapsedCallBar;
